// ── TradeTab.jsx ───────────────────────────────────────────────────────────────
// Trade Outpost UI — shown when the player is docked at a trade_outpost node
// or when the dev tool forces it open.
// Panels:
//   BUY  — shop inventory with current prices; player clicks to purchase
//   SELL — player's own inventory; click to convert to Void-Creds
// Discovery items are buy-only and cannot be sold back.
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from "react";
import { fmt } from "../utils/fmt";

// ── Sub-component: single trade row ──────────────────────────────────────────

function TradeRow({ item, owned, mode, onAction, voidCreds, qty }) {
  const isBuy  = mode === "buy";
  const price  = isBuy ? item.buyPrice : item.sellPrice;
  const total  = price * qty;
  const isDisc = item.category === "discovery";

  const canDo = isBuy
    ? voidCreds >= total && item.stock >= qty
    : owned >= qty && !isDisc;



  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "7px 10px",
      borderRadius: 7,
      background: isDisc
        ? "rgba(170,85,255,0.06)"
        : canDo ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.01)",
      border: `1px solid ${isDisc ? "rgba(170,85,255,0.2)" : canDo ? "rgba(68,255,204,0.12)" : "rgba(255,255,255,0.05)"}`,
      marginBottom: 4,
      opacity: canDo || (isBuy && isDisc && voidCreds >= total && item.stock >= qty) ? 1 : 0.45,
    }}>
      {/* Icon */}
      <span style={{ fontSize: 18, minWidth: 22, textAlign: "center" }}>{item.icon}</span>

      {/* Name + stock / owned + discovery desc */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: item.color ?? "#ddeeff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {item.name}
          {isDisc && <span style={{ marginLeft: 5, fontSize: 9, color: "#aa88ff", fontWeight: 400, textTransform: "uppercase", letterSpacing: 0.5 }}>DISCOVERY</span>}
        </div>
        {isDisc && item.desc && (
          <div style={{ fontSize: 9, color: "rgba(255,255,255,0.35)", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {item.desc}
          </div>
        )}
        {!isDisc && (
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginTop: 1 }}>
            {isBuy
              ? `Stock: ${fmt(item.stock)}  ·  Own: ${fmt(owned)}`
              : `Own: ${fmt(owned)}`}
          </div>
        )}
        {isDisc && (
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginTop: 1 }}>
            {isBuy ? `Stock: ${fmt(item.stock)}  ·  Own: ${fmt(owned)}` : "Not sellable"}
          </div>
        )}
      </div>

      {/* Price */}
      <div style={{ textAlign: "right", minWidth: 52 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: isBuy ? (isDisc ? "#cc88ff" : "#44ffcc") : "#ffaa44" }}>
          {isBuy ? `${fmt(total)} VC` : (isDisc ? "—" : `${fmt(total)} VC`)}
        </div>
        <div style={{ fontSize: 9, color: "rgba(255,255,255,0.3)" }}>
          {isBuy ? `${fmt(price)} ea` : (isDisc ? "buy only" : `${fmt(price)} ea`)}
        </div>
      </div>

      {/* Action button */}
      {isBuy ? (
        <button
          onClick={() => {
            const ok = voidCreds >= total && item.stock >= qty;
            if (ok) onAction(item);
          }}
          disabled={!(voidCreds >= total && item.stock >= qty)}
          style={{
            padding: "5px 10px",
            background: (voidCreds >= total && item.stock >= qty) ? (isDisc ? "rgba(170,85,255,0.18)" : "rgba(68,255,204,0.14)") : "rgba(255,255,255,0.04)",
            border: `1px solid ${(voidCreds >= total && item.stock >= qty) ? (isDisc ? "#aa55ff55" : "#44ffcc55") : "rgba(255,255,255,0.08)"}`,
            borderRadius: 5,
            color: (voidCreds >= total && item.stock >= qty) ? (isDisc ? "#cc88ff" : "#44ffcc") : "rgba(255,255,255,0.2)",
            fontFamily: "inherit", fontSize: 11, fontWeight: 700,
            cursor: (voidCreds >= total && item.stock >= qty) ? "pointer" : "default",
            whiteSpace: "nowrap",
          }}
        >
          BUY
        </button>
      ) : (
        <button
          onClick={() => !isDisc && canDo && onAction(item)}
          disabled={isDisc || !canDo}
          style={{
            padding: "5px 10px",
            background: (!isDisc && canDo) ? "rgba(255,170,68,0.14)" : "rgba(255,255,255,0.04)",
            border: `1px solid ${(!isDisc && canDo) ? "#ffaa4455" : "rgba(255,255,255,0.08)"}`,
            borderRadius: 5,
            color: (!isDisc && canDo) ? "#ffaa44" : "rgba(255,255,255,0.2)",
            fontFamily: "inherit", fontSize: 11, fontWeight: 700,
            cursor: (!isDisc && canDo) ? "pointer" : "default",
            whiteSpace: "nowrap",
          }}
        >
          SELL
        </button>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export function TradeTab({
  shopItems, getOwned, buyItem, sellItem,
  voidCreds, inventory, materials,
  canReset, resetCountdown, resetShop, cooldownActive,
}) {
  const [mode,     setMode]     = useState("buy");
  const [qty,      setQty]      = useState(1);
  const [category, setCategory] = useState("all");
  const [flash,    setFlash]    = useState(null);

  function showFlash(msg, ok) {
    setFlash({ msg, ok });
    setTimeout(() => setFlash(null), 1400);
  }

  function handleBuy(item) {
    const ok = buyItem(item, qty);
    showFlash(ok ? `Bought ${qty}× ${item.name}` : "Not enough VC or stock", ok);
  }

  function handleSell(item) {
    const ok = sellItem(item, qty);
    showFlash(ok ? `Sold ${qty}× ${item.name}` : "Not enough in inventory", ok);
  }

  // Category filter includes discovery
  const CATS = [["all", "All"], ["raw", "Raw"], ["material", "Smelted"], ["discovery", "Discovery"]];
  const hasDiscovery = shopItems.some(it => it.category === "discovery");
  const visibleCats  = hasDiscovery ? CATS : CATS.filter(([c]) => c !== "discovery");

  const buyList = shopItems.filter(it =>
    category === "all" || it.category === category
  );

  const sellList = shopItems.filter(it => {
    if (it.category === "discovery") return false; // discovery never sellable
    const owned = getOwned(it);
    return owned > 0 && (category === "all" || it.category === category);
  });

  const displayList = mode === "buy" ? buyList : sellList;

  const ACCENT   = "#44ffcc";
  const SELL_CLR = "#ffaa44";

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", background: "#060a10" }}>

      {/* ── Header ── */}
      <div style={{ padding: "12px 14px 10px", borderBottom: "1px solid rgba(68,255,204,0.1)", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: 1, color: "rgba(68,255,204,0.7)", fontWeight: 700 }}>TRADE OUTPOST</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: ACCENT }}>🏪 Commodity Market</div>
          </div>
          {/* Void-Creds balance */}
          <div style={{ textAlign: "right", background: "rgba(68,255,204,0.08)", border: "1px solid rgba(68,255,204,0.2)", borderRadius: 8, padding: "6px 12px" }}>
            <div style={{ fontSize: 10, color: "rgba(68,255,204,0.6)", letterSpacing: 1 }}>VOID-CREDS</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: ACCENT }}>{fmt(voidCreds)} VC</div>
          </div>
        </div>

        {/* BUY / SELL toggle */}
        <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
          {[["buy", "BUY", ACCENT], ["sell", "SELL", SELL_CLR]].map(([m, label, clr]) => (
            <button key={m} onClick={() => setMode(m)}
              style={{ flex: 1, padding: "7px 0", background: mode === m ? `${clr}18` : "transparent", border: `1px solid ${mode === m ? clr + "55" : "rgba(255,255,255,0.1)"}`, borderRadius: 6, color: mode === m ? clr : "rgba(255,255,255,0.4)", fontFamily: "inherit", fontSize: 12, fontWeight: 700, cursor: "pointer", transition: "all 0.15s" }}>
              {label}
            </button>
          ))}
        </div>

        {/* Category filter + quantity selector */}
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <div style={{ display: "flex", gap: 4, flex: 1, flexWrap: "wrap" }}>
            {visibleCats.map(([c, lbl]) => (
              <button key={c} onClick={() => setCategory(c)}
                style={{ padding: "4px 8px", background: category === c ? "rgba(255,255,255,0.08)" : "transparent", border: `1px solid ${category === c ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.06)"}`, borderRadius: 5, color: category === c ? (c === "discovery" ? "#cc88ff" : "#ddeeff") : "rgba(255,255,255,0.35)", fontFamily: "inherit", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>
                {lbl}
              </button>
            ))}
          </div>

          {/* Quantity picker */}
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", fontWeight: 700 }}>QTY</span>
            {[1, 5, 10].map(q => (
              <button key={q} onClick={() => setQty(q)}
                style={{ width: 30, height: 26, background: qty === q ? "rgba(68,255,204,0.15)" : "rgba(255,255,255,0.04)", border: `1px solid ${qty === q ? ACCENT + "44" : "rgba(255,255,255,0.08)"}`, borderRadius: 5, color: qty === q ? ACCENT : "rgba(255,255,255,0.4)", fontFamily: "inherit", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                {q}
              </button>
            ))}
          </div>
        </div>

        {/* Shop reset cooldown bar */}
        <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
          <button
            onClick={resetShop}
            disabled={cooldownActive}
            style={{
              flex: 1, padding: "5px 10px",
              background: canReset ? "rgba(68,255,204,0.12)" : "rgba(255,255,255,0.03)",
              border: `1px solid ${canReset ? "rgba(68,255,204,0.35)" : "rgba(255,255,255,0.08)"}`,
              borderRadius: 6,
              color: canReset ? ACCENT : "rgba(255,255,255,0.3)",
              fontFamily: "inherit", fontSize: 11, fontWeight: 700,
              cursor: canReset ? "pointer" : "default",
            }}
          >
            {canReset ? "🔄 Refresh Stock" : `🔄 Refresh: ${resetCountdown}`}
          </button>
        </div>
      </div>

      {/* ── Flash feedback ── */}
      {flash && (
        <div style={{ padding: "5px 14px", background: flash.ok ? "rgba(68,255,100,0.1)" : "rgba(255,60,60,0.1)", borderBottom: `1px solid ${flash.ok ? "rgba(68,255,100,0.25)" : "rgba(255,60,60,0.25)"}`, fontSize: 12, color: flash.ok ? "#66ffaa" : "#ff6666", fontWeight: 600, flexShrink: 0 }}>
          {flash.ok ? "✓" : "✗"} {flash.msg}
        </div>
      )}

      {/* ── Item list ── */}
      <div style={{ flex: 1, overflowY: "auto", padding: "10px 12px" }}>
        {displayList.length === 0 ? (
          <div style={{ textAlign: "center", color: "rgba(255,255,255,0.35)", fontSize: 13, marginTop: 40 }}>
            {mode === "sell"
              ? "Nothing in your inventory to sell here"
              : category === "discovery"
                ? "No discovery items — research the Discovery Catalog to unlock"
                : "No items in this category"}
          </div>
        ) : (
          displayList.map((item, i) => (
            <TradeRow
              key={item.id + i}
              item={item}
              owned={getOwned(item)}
              mode={mode}
              qty={qty}
              voidCreds={voidCreds}
              onAction={mode === "buy" ? handleBuy : handleSell}
            />
          ))
        )}
      </div>

      {/* ── Footer ── */}
      <div style={{ padding: "8px 12px", borderTop: "1px solid rgba(255,255,255,0.06)", flexShrink: 0 }}>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", textAlign: "center" }}>
          {mode === "sell"
            ? "Sell price is a fraction of buy value · Prices vary by sector"
            : "Prices fluctuate ±15% per sector · Discovery items: buy only"}
        </div>
      </div>
    </div>
  );
}
